import React, { useState, useContext, useMemo, useEffect } from 'react';
import classNames from 'classnames/bind';
import qs from 'qs';
import axios from 'axios';

import { CountryContext } from 'context/CountryContext';
import { SearchContext } from 'context/SearchContext';
import { StaticTextContext } from 'context/StaticTextContext';

import { setBrowserHistory } from 'utils/historyPushSearch';
import { getCarManufactures } from 'utils/getCarAutosuggestions';
import { currentLanguage } from 'utils/currentLanguage';
import { isCountryPage } from 'utils/browserHistory';

import countriesEn from './countries_en.json';
import countriesFr from './countries_fr.json';
import countriesTh from './countries_th.json';

import ArrowDown from 'media/icons/arrow-down.icon.svg';
import styles from './CountrySelector.module.scss';

const cx = classNames.bind(styles);
const urlPattern = new RegExp(
  '^(http://www.|https://www.|http://|https://)?[a-z0-9]+([-.]{1}[a-z0-9]+)*.[a-z]{2,5}(:[0-9]{1,5})?(/.*)?$'
);
const getCurrentOptions = (lang) => {
  switch (lang) {
    case 'fr-com':
      return countriesFr;
    case 'en-th':
      return countriesTh;
    default:
      return countriesEn;
  }
};

const CountrySelector = () => {
  const [countries, setCountries] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countriesData = getCurrentOptions(currentLanguage);
    if (!isCountryPage) {
      setCountries(countriesData.countries);
      setLoading(false);
      return;
    }
    // Case Country Page
    const countryPagePath = window.location.href.split('country_page/')[1];
    axios
      .get(
        `/${currentLanguage}/rest/ta/settings?type=country-page&page=${countryPagePath}`
      )
      .then((response) => {
        const { countries } =
          response.data.countries && response.data.countries.length
            ? response.data
            : countriesData;
        setCountries(countries);
      })
      .catch(() => setCountries(countriesData.countries))
      .finally(() => setLoading(false));
  }, []);

  const queryValues = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  const [countryValue, setCountryValue] = useState(
    queryValues.country || '.com'
  );

  const { setCurrentCountry, setCountryHash } = useContext(CountryContext);
  const { vehicleType, setManuCar, setManuTruck } = useContext(SearchContext);
  const contextSearch = useContext(SearchContext);
  const { labels } = useContext(StaticTextContext);

  const setManuContext = (data, vehicleType) => {
    if (vehicleType === 'P') {
      setManuCar(data);
    } else if (vehicleType === 'O') {
      setManuTruck(data);
    }
  };

  const onSelectChange = (event) => {
    const targetValue = event.target.value;
    const currentCountryParsed = targetValue.split('_')[0];
    const { hash, search } = window.location;

    if (currentCountryParsed.match(urlPattern)) {
      window.location.href = currentCountryParsed;
      return;
    }

    setCurrentCountry(currentCountryParsed);
    setCountryHash(targetValue);

    setCountryValue(targetValue);
    setBrowserHistory({ country: event.target.value }, search, hash);
    setManuCar(null);
    setManuTruck(null);

    // @todo: move it to autosuggestion manu.
    contextSearch.setIsSelectedCar(false);
    contextSearch.setIsSelectedModel(false);
    contextSearch.setIsSelectedManu(false);
    contextSearch.setIsErrorCar(false);
    contextSearch.setIsErrorManu(false);
    contextSearch.setIsErrorPart(false);
    contextSearch.setIsErrorModel(false);
    contextSearch.setVinNumber('');
    contextSearch.setSearchCarQuery(null);
    contextSearch.setPartName('');
    contextSearch.setPartId(null);
    contextSearch.setCarTypeName('');
    contextSearch.setCarName('');
    contextSearch.setCarId(null);
    contextSearch.setManuName('');
    contextSearch.setManuId(null);
    contextSearch.setModelName('');
    contextSearch.setModelId(null);
    contextSearch.setReference('');
    contextSearch.setIsEditedManuField(false);
    contextSearch.setIsEditedModelField(false);
    contextSearch.setIsEditedCarField(false);
    contextSearch.setIsEditedVinVehicleField(false);
    contextSearch.setIsEditedPartField(false);

    getCarManufactures(currentCountryParsed, vehicleType)
      .then(({ data: response }) => {
        const manufactures = response.data.array ? response.data.array : [];
        setManuContext(manufactures, vehicleType);
      })
      .catch(() => {
        setManuContext([], vehicleType);
      });
  };

  return (
    <div className={cx('root')}>
      <label className={cx('label')} htmlFor="country-select">
        Country
      </label>
      <div className={cx('container')}>
        <select
          id="country-select"
          className={cx('select', 'is-filled')}
          value={countryValue}
          onChange={onSelectChange}
        >
          <option value=".com" hidden="hidden" disabled>
            {labels.countrySelectorPlaceholder}
          </option>

          {loading ? (
            <option disabled>{labels.loading}...</option>
          ) : (
            countries &&
            countries.map(({ optionGroupLabel, options }, i) => (
              <optgroup key={i} label={optionGroupLabel}>
                {options.map(({ value, label }, j) => (
                  <option value={value} key={j}>
                    {label}
                  </option>
                ))}
              </optgroup>
            ))
          )}
        </select>
        <ArrowDown className={cx('icon')} />
      </div>
    </div>
  );
};

export default CountrySelector;



// WEBPACK FOOTER //
// ./src/components/CountrySelector/CountrySelector.js